import { formatNumber } from '@angular/common';
import { Pipe, PipeTransform } from '@angular/core';
import { Material } from '../classes/material.class';

type MaterialProperty =
    | 'name'
    | 'type'
    | 'supplier'
    | 'color'
    | 'colorName'
    | 'colorRal'
    | 'typeName'
    | 'certificate_number'
    | 'thickness'
    | 'consumption'
    | 'width'
    | 'usage'
    | 'quantity_unit';

const getMainRawMaterial = (material?: Material | null) => material?.raw_materials?.find(rm => rm.material_type?.main);

// Shared with production-table.service.ts, which needs the same "1,2 kg/m²" label outside of a
// template context (it builds column headers, not bindings).
export function getMaterialUsageLabel(material?: Material | null): string {
    const mainRawMaterial = getMainRawMaterial(material);
    const usage = mainRawMaterial?.pivot?.consumption ?? mainRawMaterial?.consumption;

    return usage ? `${formatNumber(usage, 'nl')} ${mainRawMaterial?.consumption_type?.name}` : '';
}

@Pipe({
    name: 'materialProperty',
    standalone: false,
})
export class MaterialPropertyPipe implements PipeTransform {
    transform(material: Material | null, property: MaterialProperty, type?: string | null): string {
        if (!material) {
            return '';
        }

        const mainRawMaterial = getMainRawMaterial(material);
        const color = mainRawMaterial?.color;

        switch (property) {
            case 'name':
                return material.name || '';

            case 'type':
                return mainRawMaterial?.material_type?.name || '';

            case 'usage':
                return getMaterialUsageLabel(material);

            case 'thickness':
                return formatNumber(mainRawMaterial?.thickness, 'nl') || '';

            case 'consumption':
                let consumption = mainRawMaterial?.pivot?.consumption ?? mainRawMaterial?.consumption;
                return consumption ? formatNumber(consumption, 'nl') : '';

            case 'width':
                return mainRawMaterial?.width && mainRawMaterial.width !== 0
                    ? formatNumber(mainRawMaterial.width, 'nl')
                    : null;

            case 'quantity_unit':
                return (mainRawMaterial?.pivot?.consumption ?? mainRawMaterial?.consumption)
                    ? 'kg'
                    : mainRawMaterial?.width
                      ? 'm'
                      : '';

            case 'typeName':
                return `${material.name}${type !== material.name ? ` ${type}` : ''}`;

            case 'supplier':
                return mainRawMaterial?.supplier?.name || '';

            case 'certificate_number':
                return mainRawMaterial?.certificate_number || '';

            case 'color':
                return color?.color || '';

            case 'colorName':
                return color?.name ?? '';

            case 'colorRal':
                if (mainRawMaterial?.color) {
                    return mainRawMaterial.color.ral || '';
                }
                return '';

            default:
                return '';
        }
    }
}
